import React, { useState, useRef } from "react";
import { App, Modal, Input } from "antd";
import {
  UploadOutlined, FilePdfOutlined, LockOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled,
  EyeOutlined, EyeInvisibleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./PasswordPdf.css";

const AddPasswordPdf = () => {
  const { message } = App.useApp();
  const [file,        setFile]        = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [lockedFileName, setLockedFileName] = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [password,    setPassword]    = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [pwdError,    setPwdError]    = useState("");
  const fileRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const pickFile = (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f);
    setDownloadUrl("");
    setLockedFileName("");
    setDownloading(false);
  };

  const reset = () => {
    setFile(null);
    setDownloadUrl("");
    setLockedFileName("");
    setDownloading(false);
    setPassword("");
    setConfirm("");
  };

  const openModal = () => {
    if (!file) return;
    setPassword(""); setConfirm(""); setPwdError("");
    setShowModal(true);
  };

  const handleLock = async () => {
    if (!password.trim()) { setPwdError("Password cannot be empty."); return; }
    if (password !== confirm) { setPwdError("Passwords do not match."); return; }
    setShowModal(false);
    setUploading(true);
    setDownloadUrl("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");
    formData.append("Password", password);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/lock-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!res.data.error && res.data.data?.downloadUrl) {
        setDownloadUrl(res.data.data.downloadUrl);
        setLockedFileName(res.data.data.fileName || "protected.pdf");
        message.success("PDF locked successfully!");
      } else {
        message.error(res.data.message || "Failed to lock PDF.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const downloadLockedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);

    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: lockedFileName || "protected.pdf",
      });
    } catch {
      message.error("Unable to download protected PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner">

          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#fff0f0" }}>
              <LockOutlined style={{ color: "#e5322d" }} />
            </div>
            <div>
              <h1 className="pt-title">Add Password</h1>
              <p className="pt-subtitle">Protect your PDF file with a password. Only authorised users can open it.</p>
            </div>
          </div>

          {!file && !downloadUrl && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && !downloadUrl && (
            <>
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>
              <button className="pt-action-btn" onClick={openModal} disabled={uploading}>
                {uploading ? <span className="pt-spinner" /> : <LockOutlined />}
                {uploading ? "Locking PDF…" : "Set Password & Lock"}
              </button>
            </>
          )}

          {downloadUrl && (
            <div className="pt-success">
              <CheckCircleFilled className="pt-success-icon" />
              <div className="pt-success-title">PDF Locked!</div>
              <p className="pt-success-sub">Your PDF is now password-protected.</p>
              <div className="pt-success-actions">
                <button
                  type="button"
                  className="pt-download-btn"
                  onClick={downloadLockedPdf}
                  disabled={downloading}
                >
                  {downloading ? <span className="pt-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download Protected PDF"}
                </button>
                <button className="pt-restart-btn" onClick={reset}>Lock another</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password Modal */}
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={handleLock}
        okText="Lock PDF"
        okButtonProps={{ style: { background: "#e5322d", borderColor: "#e5322d" } }}
        title={<span style={{ fontFamily: "Poppins", fontWeight: 700 }}>🔒 Set Password</span>}
        centered
      >
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20 }}>
          Enter a password to protect this PDF file.
        </p>
        <div className="pwd-field">
          <label className="pwd-label">Password</label>
          <Input.Password
            value={password}
            onChange={(e) => { setPassword(e.target.value); setPwdError(""); }}
            placeholder="Enter password"
            size="large"
            iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          />
        </div>
        <div className="pwd-field">
          <label className="pwd-label">Confirm Password</label>
          <Input.Password
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setPwdError(""); }}
            placeholder="Re-enter password"
            size="large"
            iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          />
        </div>
        {pwdError && <div className="pwd-error">{pwdError}</div>}
      </Modal>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default AddPasswordPdf;
